import type {
  ImageModel,
  ImageModelCapability,
  ImagePrice,
  ProviderPrice,
  RefreshedProviderEndpoint,
  SaveImageModel,
} from "@nlc/shared-types";

import { formatUnitUsd } from "../formatUsd";

export type CapabilityName = "edit" | "textToImage";

export const CAPABILITY_LABELS: Record<CapabilityName, string> = {
  edit: "Edit",
  textToImage: "Generation",
};

const PRICE_EPSILON = 1e-9;

export function toSaveModel({
  updatedAt: _updatedAt,
  updatedBy: _updatedBy,
  ...model
}: ImageModel): SaveImageModel {
  return model;
}

export function isSameModel(a: SaveImageModel, b: SaveImageModel): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function formatImagePrice({ inputImages, pricing }: ImagePrice): string {
  const base = (() => {
    switch (pricing.kind) {
      case "perImage": {
        return `${formatUnitUsd(pricing.usd)} / image`;
      }
      case "perImageByArea": {
        return `from ${formatUnitUsd(Math.min(...pricing.tiers.map((tier) => tier.usd)))} / image`;
      }
      case "perMegapixel": {
        return pricing.firstUsd === pricing.additionalUsd
          ? `${formatUnitUsd(pricing.firstUsd)} / MP`
          : `${formatUnitUsd(pricing.firstUsd)} first MP, ${formatUnitUsd(pricing.additionalUsd)} / MP after`;
      }
    }
  })();

  return inputImages
    ? `${base} + ${formatUnitUsd(inputImages.usd)} per input image`
    : base;
}

function storedUnitPrice({ pricing }: ImagePrice): {
  unit: string;
  value: number;
} | null {
  if (pricing.kind === "perImage") return { unit: "image", value: pricing.usd };
  if (pricing.kind === "perMegapixel") {
    return { unit: "megapixel", value: pricing.firstUsd };
  }

  return null;
}

export function differsFromProviderPrice(
  price: ImagePrice,
  providerPrice: ProviderPrice,
): boolean {
  const stored = storedUnitPrice(price);

  return (
    !stored ||
    stored.unit !== providerPrice.unit ||
    Math.abs(stored.value - providerPrice.unitPrice) > PRICE_EPSILON
  );
}

export function formatProviderPrice({
  unit,
  unitPrice,
}: ProviderPrice): string {
  return `${formatUnitUsd(unitPrice)} / ${unit === "megapixel" ? "MP" : unit}`;
}

export function acceptProviderPrice(
  capability: ImageModelCapability,
  refreshed: RefreshedProviderEndpoint,
): ImageModelCapability {
  const { providerPrice } = refreshed;

  if (!providerPrice) return capability;

  return {
    ...capability,
    price: {
      ...capability.price,
      pricing:
        providerPrice.unit === "megapixel"
          ? {
              additionalUsd: providerPrice.unitPrice,
              firstUsd: providerPrice.unitPrice,
              kind: "perMegapixel",
            }
          : { kind: "perImage", usd: providerPrice.unitPrice },
      source: "fal",
      updatedAt: new Date(),
      verified: false,
    },
    providerPrice,
  };
}
